import { useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useI18n } from '../../lib/i18n';
import { setQuery, useLocation } from '../../lib/router';
import { AsyncButton, Button, CopyField, Field, Input, Modal, PageHeader, Tabs, useToast } from '../../ui/components';
import { ResourcePage } from '../../ui/schema';

interface TabDef { id: string; resource: string; columns?: string[]; label?: string; hide?: string[]; query?: Record<string, string> }

function TabbedResources({ title, tabs, subtitle, extra }: { title: string; subtitle?: string; tabs: TabDef[]; extra?: (tab: TabDef) => { rowActions?: (row: any, reload: () => void) => any; actions?: any } }) {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const current = tabs.find((t) => t.id === query.get('tab')) ?? tabs[0];
  const more = extra?.(current) ?? {};
  return (
    <div className="page">
      <PageHeader title={title} subtitle={subtitle} />
      {tabs.length > 1 && <Tabs value={current.id} onChange={(v) => setQuery({ tab: v })} tabs={tabs.map((t) => ({ id: t.id, label: t.label ?? i18n.entity(t.resource) }))} />}
      <ResourcePage key={current.id} slug={gym.slug} resource={current.resource} columns={current.columns} hide={current.hide} query={current.query} rowActions={more.rowActions} actions={more.actions} />
    </div>
  );
}

export function LessonsAdminPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  return (
    <TabbedResources title={i18n.t('nav.lessons')} subtitle={i18n.t('offer.lessons_help')} tabs={[
      { id: 'types', resource: 'lesson_types', columns: ['name', 'duration_min', 'capacity', 'token_type_id', 'tokens_required', 'visibility'] },
      { id: 'series', resource: 'lesson_series', columns: ['lesson_type_id', 'location_id', 'trainer_person_id', 'start_time', 'start_date', 'end_date', 'is_active'] },
    ]} extra={(tab) => ({
      rowActions: tab.resource === 'lesson_series' ? (row, reload) => (
        <AsyncButton size="sm" variant="secondary" onClick={async () => { const r = await api(`${T(gym.slug)}/admin/series/${row.id}/generate`, { body: {} }); toast.ok(i18n.t('offer.generated', { count: r.created ?? 0 })); reload(); }}>{i18n.t('offer.generate')}</AsyncButton>
      ) : undefined,
    })} />
  );
}

export function MembershipsAdminPage() {
  const i18n = useI18n();
  return (
    <TabbedResources title={i18n.t('nav.memberships')} subtitle={i18n.t('offer.memberships_help')} tabs={[
      { id: 'plans', resource: 'membership_plans', columns: ['name', 'plan_type', 'price_cents', 'billing_interval', 'min_term_months', 'is_active'] },
      { id: 'memberships', resource: 'memberships', columns: ['person_id', 'plan_id', 'status', 'start_date', 'price_cents', 'cancel_effective_date'] },
    ]} />
  );
}

export function TokensAdminPage() {
  const i18n = useI18n();
  return (
    <TabbedResources title={i18n.t('nav.tokens')} subtitle={i18n.t('offer.tokens_help')} tabs={[
      { id: 'types', resource: 'token_types' },
      { id: 'upgrades', resource: 'token_upgrades' },
      { id: 'batches', resource: 'token_batches', columns: ['person_id', 'token_type_id', 'quantity', 'remaining', 'expires_at', 'source'] },
      { id: 'ledger', resource: 'token_ledger', columns: ['person_id', 'token_type_id', 'delta', 'reason', 'created_at'] },
    ]} />
  );
}

export function ShopAdminPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [gift, setGift] = useState<{ amount: string; name: string; email: string } | null>(null);
  const [issued, setIssued] = useState<{ code: string } | null>(null);
  return (
    <>
      <TabbedResources title={i18n.t('nav.shop')} tabs={[
        { id: 'products', resource: 'products', columns: ['name', 'product_type', 'price_cents', 'visibility', 'is_active'] },
        { id: 'stock', resource: 'stock_levels' },
        { id: 'orders', resource: 'orders', columns: ['number', 'person_id', 'status', 'total_cents', 'discount_code', 'created_at'] },
        { id: 'discounts', resource: 'discount_codes', columns: ['code', 'kind', 'value', 'applies_to', 'valid_until', 'redeemed_count', 'is_active'] },
        { id: 'gift_cards', resource: 'gift_cards', columns: ['code', 'initial_cents', 'balance_cents', 'status', 'expires_at'] },
      ]} extra={(tab) => ({
        actions: tab.resource === 'gift_cards'
          ? <Button onClick={() => setGift({ amount: '50', name: '', email: '' })}>+ {i18n.t('offer.issue_gift_card')}</Button>
          : undefined,
      })} />
      {gift && (
        <Modal title={i18n.t('offer.issue_gift_card')} onClose={() => { setGift(null); setIssued(null); }} footer={
          issued ? null : (
            <AsyncButton disabled={!Number(gift.amount)} onClick={async () => {
              const r = await api(`${T(gym.slug)}/admin/gift-cards`, { body: { amountCents: Math.round(Number(gift.amount) * 100), recipientName: gift.name, recipientEmail: gift.email } });
              setIssued({ code: r.code });
              toast.ok(i18n.t('offer.gift_card_created'));
            }}>{i18n.t('common.create')}</AsyncButton>
          )
        }>
          {issued ? (
            <>
              <p>{i18n.t('offer.gift_card_code')}</p>
              <CopyField value={issued.code} />
              <p className="small muted">{i18n.t('offer.gift_card_hint')}</p>
            </>
          ) : (
            <div className="grid-2">
              <Field label={i18n.field('amount_cents')}><Input type="number" min={1} step="0.01" value={gift.amount} onChange={(e) => setGift({ ...gift, amount: e.target.value })} /></Field>
              <Field label={i18n.field('recipient_name')}><Input value={gift.name} onChange={(e) => setGift({ ...gift, name: e.target.value })} /></Field>
              <Field label={i18n.field('recipient_email')} className="span-2"><Input type="email" value={gift.email} onChange={(e) => setGift({ ...gift, email: e.target.value })} /></Field>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

export function EventsAdminPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [checkIn, setCheckIn] = useState<string | null>(null);
  const [code, setCode] = useState('');
  return (
    <>
      <TabbedResources title={i18n.t('nav.events')} tabs={[
        { id: 'events', resource: 'events', columns: ['title', 'starts_at', 'status', 'capacity', 'registered_count'] },
        { id: 'tickets', resource: 'event_ticket_types' },
        { id: 'registrations', resource: 'event_registrations', columns: ['event_id', 'person_id', 'ticket_type_id', 'status', 'ticket_code', 'checked_in_at'] },
      ]} extra={(tab) => ({
        rowActions: tab.resource === 'events' ? (row, reload) => (
          <>
            {row.status === 'published' && <Button size="sm" variant="secondary" onClick={() => { setCheckIn(row.id); setCode(''); }}>{i18n.t('offer.check_in')}</Button>}
            {['published', 'draft'].includes(row.status) && (
              <AsyncButton size="sm" variant="ghost" onClick={async () => {
                const reason = window.prompt(i18n.t('offer.cancel_event_reason'));
                if (!reason) return;
                await api(`${T(gym.slug)}/admin/events/${row.id}/cancel`, { body: { reason } });
                reload();
              }}>{i18n.t('common.cancel')}</AsyncButton>
            )}
          </>
        ) : undefined,
      })} />
      {checkIn && (
        <Modal title={i18n.t('offer.check_in')} onClose={() => setCheckIn(null)}>
          <form className="row gap-sm" onSubmit={(e) => e.preventDefault()}>
            <Field label={i18n.field('ticket_code')}><Input autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} /></Field>
            <AsyncButton disabled={!code} onClick={async () => {
              const r = await api(`${T(gym.slug)}/admin/events/${checkIn}/check-in`, { body: { code } });
              toast.ok(i18n.t('offer.checked_in', { name: r.name ?? '' }));
              setCode('');
            }}>{i18n.t('offer.check_in')}</AsyncButton>
          </form>
        </Modal>
      )}
    </>
  );
}

export function LocationsAdminPage() {
  const i18n = useI18n();
  return (
    <TabbedResources title={i18n.t('nav.locations')} subtitle={i18n.t('offer.locations_help')} tabs={[
      { id: 'locations', resource: 'locations', columns: ['name', 'city', 'phone', 'is_active'] },
      { id: 'rooms', resource: 'rooms', columns: ['name', 'location_id', 'capacity', 'bookable'] },
      { id: 'closures', resource: 'location_exceptions', columns: ['location_id', 'start_date', 'end_date', 'closed', 'reason'] },
    ]} />
  );
}

export function GenericResourcePage({ resource }: { resource: string }) {
  const gym = useGym();
  const i18n = useI18n();
  return (
    <div className="page">
      <PageHeader title={i18n.entity(resource)} />
      <ResourcePage slug={gym.slug} resource={resource} />
    </div>
  );
}
